"use client";
import React, { useState, useTransition } from "react";
import { useRouter } from "next/navigation";
import { useForm } from "react-hook-form";
import { formValidationSchemaSignUp, type ISchemaSignUp } from "~/globals/auth";
import { zodResolver } from "@hookform/resolvers/zod";
import { supabaseClient } from "~/globals/client";
import { notifications } from "@mantine/notifications";
import InputValidationError from "~/components/TextInput/InputValidationError";
import { type Account, AccountType } from "@prisma/client";
import Button from "~/components/Button/Button";
import ValidatedBasicTextInput from "~/components/TextInput/ValidatedBasicTextInput";

type Props = {
  checkInvestorCodeValidity: (code: string) => Promise<boolean>;
  createAccount: (
    supabaseUserId: string,
    data: ISchemaSignUp,
  ) => Promise<Account>;
};
function SignUpForm({ checkInvestorCodeValidity, createAccount }: Props) {
  const [isCheckingInvestorCode, setCheckingTransition] = useTransition();
  const [isSigningUp, setSigningUp] = useState(false);
  const router = useRouter();
  const methods = useForm<ISchemaSignUp>({
    resolver: zodResolver(formValidationSchemaSignUp),
    mode: "onChange",
    shouldUseNativeValidation: false,
  });

  const { handleSubmit, watch, formState, register } = methods;
  const { errors } = formState;

  const accountType = watch("accountType");

  async function finalizeSubmit(data: ISchemaSignUp) {
    try {
      setSigningUp(true);
      const { error, data: signupResponse } = await supabaseClient.auth.signUp({
        email: data.email,
        password: data.password,
        options: {
          emailRedirectTo: `${location.origin}/auth/callback`,
          data: {
            fullName: data.fullName,
          },
        },
      });
      if (error) throw error;
      console.log("createdSupabaseUserData", signupResponse);
      if (signupResponse?.user?.id) {
        await createAccount(signupResponse.user.id, data);
      }
      notifications.show({
        title: "Signup successful",
        message: "We have sent you an email to confirm your account",
      });
      router.push("/auth/signin");
    } catch (err) {
      const error = err as Error;
      notifications.show({
        title: "",
        message: error.message,
        color: "red",
      });
    } finally {
      setSigningUp(false);
    }
  }

  function handleSignUp(data: ISchemaSignUp) {
    console.log("data", data);
    if (data.accountType === AccountType.INVESTOR) {
      setCheckingTransition(async () => {
        const check = await checkInvestorCodeValidity(data.investorCode);
        console.log("check", check);
        if (!check) {
          notifications.show({
            title: "Invalid investor code",
            message:
              "The investor code you entered is invalid. Please contact us to get an investor code.",
            color: "red",
          });
          return;
        } else {
          void finalizeSubmit(data);
        }
      });
    } else {
      void finalizeSubmit(data);
    }
  }

  return (
    <form className="space-y-6" onSubmit={handleSubmit(handleSignUp)}>
      <fieldset>
        <legend className="font-bold">Account type</legend>
        <div className="mt-1 flex space-x-4">
          <div className="flex items-center gap-x-3">
            <input
              {...register("accountType")}
              id="accountTypeStartup"
              value={AccountType.STARTUP}
              type="radio"
              className="h-4 w-4"
            />
            <label htmlFor="accountTypeStartup" className="block ">
              Startup
            </label>
          </div>
          <div className="flex items-center gap-x-3">
            <input
              {...register("accountType")}
              id="accountTypeInvestor"
              value={AccountType.INVESTOR}
              type="radio"
              className="h-4 w-4"
            />
            <label htmlFor="push-email" className="block">
              Investor
            </label>
          </div>
        </div>
        {errors.accountType?.message && (
          <InputValidationError>
            {errors.accountType.message}
          </InputValidationError>
        )}
      </fieldset>

      {accountType === AccountType.INVESTOR && (
        <>
          <div>
            <label
              htmlFor="investorCode"
              className="block text-sm font-medium leading-6 text-gray-900"
            >
              Investor code
            </label>
            <ValidatedBasicTextInput
              {...register("investorCode")}
              type="text"
              autoComplete="off"
              id="investorCode"
              className="w-full"
              errorMessage={
                "investorCode" in errors && errors.investorCode?.message
                  ? errors.investorCode.message
                  : undefined
              }
            />
          </div>

          <div>
            <label
              htmlFor="vcName"
              className="block text-sm font-medium leading-6 text-gray-900"
            >
              VC Name
            </label>
            <ValidatedBasicTextInput
              {...register("vcName")}
              type="text"
              autoComplete="off"
              id="vcName"
              className="w-full"
              errorMessage={
                "vcName" in errors && errors.vcName?.message
                  ? errors.vcName.message
                  : undefined
              }
            />
          </div>
        </>
      )}

      <div>
        <label
          htmlFor="name"
          className="block text-sm font-medium leading-6 text-gray-900"
        >
          Full name
        </label>
        <ValidatedBasicTextInput
          {...register("fullName")}
          type="text"
          autoComplete="name"
          id="name"
          className="w-full"
          errorMessage={errors.fullName?.message}
        />
      </div>

      <div>
        <label
          htmlFor="email"
          className="block text-sm font-medium leading-6 text-gray-900"
        >
          Email address
        </label>
        <ValidatedBasicTextInput
          {...register("email")}
          type="email"
          autoComplete="email"
          id="email"
          className="w-full"
          errorMessage={errors.email?.message}
        />
      </div>

      <div>
        <label
          htmlFor="password"
          className="block text-sm font-medium leading-6 text-gray-900"
        >
          Password
        </label>
        <ValidatedBasicTextInput
          {...register("password")}
          type="password"
          autoComplete="current-password"
          id="password"
          className="w-full"
          errorMessage={errors.password?.message}
        />
      </div>
      <div>
        <label
          htmlFor="passwordConfirmation"
          className="block text-sm font-medium leading-6 text-gray-900"
        >
          Confirm password
        </label>
        <ValidatedBasicTextInput
          {...register("passwordConfirmation")}
          type="password"
          autoComplete="off"
          id="passwordConfirmation"
          className="w-full"
          errorMessage={errors.passwordConfirmation?.message}
        />
      </div>

      <div>
        <Button
          variant="primary"
          type="submit"
          className="w-full"
          disabled={isCheckingInvestorCode}
        >
          {isCheckingInvestorCode && "Checking investor code..."}
          {isSigningUp && "Signing up..."}
          {!isCheckingInvestorCode && !isSigningUp && "Sign up"}
        </Button>
      </div>
    </form>
  );
}

export default SignUpForm;
